import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DropdownProps<T> {
  id: string;
  label: string;
  options: T[];
  value: T;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string | number;
  onChange: (value: T) => void;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export function Dropdown<T>({
  id,
  label = '',
  options,
  value,
  getOptionLabel,
  getOptionValue,
  onChange,
  className = '',
  searchable = false,
  disabled = false,
  placeholder = 'Select an option',
  error,
  size = 'md',
  variant = 'default',
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
  if (!isOpen) return;

  // wait for the options to render
  requestAnimationFrame(() => {
    const key = value != null ? getOptionValue(value) : null;
    if (key == null) return;

    const optionEl = optionRefs.current.get(key);
    const listEl = listRef.current;
    if (!optionEl || !listEl) return;

    // Place the selected option roughly in the middle of the viewport
    const targetTop = optionEl.offsetTop - (listEl.clientHeight / 2) + (optionEl.clientHeight / 2);
    listEl.scrollTop = Math.max(0, targetTop);
  });
}, [isOpen, value, getOptionValue]);

  // Reset search query when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option: T) => {
    onChange(option);
    setIsOpen(false);
  };

  const filteredOptions = searchable
    ? options.filter((option) =>
        getOptionLabel(option).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const sizeClasses = {
    sm: 'min-h-11 px-2 py-1 text-xs',
    md: 'min-h-11 px-3 py-2 text-sm',
    lg: 'min-h-11 px-4 py-3 text-base',
  };

  const variantClasses = {
    default: 'bg-surface text-content shadow-sm ring-1 ring-line hover:bg-surface-sunken',
    outline: 'bg-transparent text-content border border-line hover:bg-surface-sunken',
    ghost: 'bg-transparent text-content hover:bg-surface-sunken',
  };

  return (
    <div className={`w-full md:flex md:justify-center relative ${className}`} ref={dropdownRef}>
      <div className="space-y-1">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-content">
            {label}
          </label>
        )}
        <button
          id={id}
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`inline-flex w-full justify-between rounded-md ${
            sizeClasses[size]
          } font-medium ${variantClasses[variant]} ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${error ? 'ring-2 ring-red-500' : ''} transition-all duration-200 ease-in-out`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">
            {value ? getOptionLabel(value) : placeholder}
          </span>
          <ChevronDown
            className={`ml-2 h-5 w-5 text-content-muted transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-10 mt-1 w-full rounded-md bg-surface shadow-lg ring-1 ring-line focus:outline-none"
          >
            {searchable && (
              <div className="px-3 py-2 border-b border-line">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-content-muted" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full min-h-11 pl-8 pr-3 py-1 text-sm bg-surface text-content placeholder:text-content-muted border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div ref={listRef} className="max-h-60 overflow-auto py-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = getOptionValue(option) === getOptionValue(value);
                  return (
                    <motion.div
                      key={getOptionValue(option)}
                        ref={(el) => {
                          const k = getOptionValue(option);
                          if (el) {
                            optionRefs.current.set(k, el);
                          } else {
                            optionRefs.current.delete(k);
                          }
                        }}
                      
                      onClick={() => handleSelect(option)}
                      className={`cursor-pointer flex min-h-11 items-center justify-between px-4 py-2 text-sm transition-colors hover:bg-surface-sunken ${
                        isSelected ? 'bg-accent/15 text-accent font-medium' : 'text-content'
                      }`}
                    >
                      <span className="block truncate font-medium">
                        {getOptionLabel(option)}
                      </span>
                      {isSelected && (
                        <Check className="h-4 w-4 text-accent" />
                      )}
                    </motion.div>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-sm text-content-muted text-center">
                  No options found
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}